import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { PageHero } from "./PageHero";
import { CTASection } from "./CTASection";
import { CityLinksStrip } from "./CityLinksStrip";

type Section =
  | { type: "prose"; heading: string; body: string | string[] }
  | { type: "list"; heading: string; intro?: string; items: { label: string; desc?: string }[] }
  | { type: "table"; heading: string; intro?: string; columns: string[]; rows: string[][] }
  | { type: "swatches"; heading: string; intro?: string; swatches: { name: string; color: string; desc?: string }[] }
  | { type: "links"; heading: string; intro?: string; links: { href: string; label: string; desc?: string }[] };

type Props = {
  eyebrow?: string;
  title: string;
  intro: string;
  image: string;
  sections: Section[];
  backHref?: string;
  backLabel?: string;
};

export function ResourceArticle({ eyebrow = "Resource", title, intro, image, sections, backHref = "/resources", backLabel = "All Resources" }: Props) {
  return (
    <>
      <PageHero eyebrow={eyebrow} title={title} body={intro} image={image} showCTA={false} />
      <article className="container-x py-16 md:py-20 max-w-3xl">
        <Link to={backHref} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-copper hover:text-copper-hover">
          <ArrowLeft className="h-3.5 w-3.5" /> {backLabel}
        </Link>

        <div className="mt-10 space-y-14">
          {sections.map((s, i) => (
            <section key={i}>
              <h2 className="text-2xl md:text-3xl text-silver-light">{s.heading}</h2>
              <span className="copper-rule mt-4 block" />

              {s.type === "prose" && (
                <div className="mt-6 space-y-4 text-silver leading-relaxed">
                  {(Array.isArray(s.body) ? s.body : [s.body]).map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                </div>
              )}

              {s.type === "list" && (
                <div className="mt-6 space-y-4">
                  {s.intro && <p className="text-silver leading-relaxed">{s.intro}</p>}
                  <ul className="space-y-3">
                    {s.items.map((it, j) => (
                      <li key={j} className="border-l-2 border-copper pl-4">
                        <p className="text-silver-light font-semibold">{it.label}</p>
                        {it.desc && <p className="text-silver text-sm mt-1">{it.desc}</p>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {s.type === "table" && (
                <div className="mt-6 space-y-4">
                  {s.intro && <p className="text-silver leading-relaxed">{s.intro}</p>}
                  <div className="overflow-x-auto rounded-sm border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-jet">
                        <tr>
                          {s.columns.map((c, j) => (
                            <th key={j} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-copper">
                              {c}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {s.rows.map((row, j) => (
                          <tr key={j} className={j % 2 === 0 ? "bg-charcoal" : "bg-charcoal/60"}>
                            {row.map((cell, k) => (
                              <td key={k} className={`px-4 py-3 align-top ${k === 0 ? "text-silver-light font-medium" : "text-silver"}`}>
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {s.type === "swatches" && (
                <div className="mt-6 space-y-4">
                  {s.intro && <p className="text-silver leading-relaxed">{s.intro}</p>}
                  <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                    {s.swatches.map((sw, j) => (
                      <div key={j} className="rounded-sm border border-border bg-charcoal overflow-hidden">
                        <div className="aspect-[4/3] w-full" style={{ background: sw.color }} aria-hidden />
                        <div className="p-4">
                          <p className="text-silver-light font-semibold">{sw.name}</p>
                          {sw.desc && <p className="text-silver text-sm mt-1">{sw.desc}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {s.type === "links" && (
                <div className="mt-6 space-y-4">
                  {s.intro && <p className="text-silver leading-relaxed">{s.intro}</p>}
                  <ul className="space-y-3">
                    {s.links.map((lk, j) => (
                      <li key={j} className="border-l-2 border-copper pl-4">
                        <a href={lk.href} className="text-silver-light font-semibold hover:text-copper transition-colors">
                          {lk.label} <span aria-hidden>→</span>
                        </a>
                        {lk.desc && <p className="text-silver text-sm mt-1">{lk.desc}</p>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          ))}
        </div>
      </article>
      <CityLinksStrip context="blog" />
      <CTASection />
    </>
  );
}
